import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Grid,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  PlayCircleOutline as PlayIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export const CTASection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/auth');
  };

  const handleBecomeCaregiver = () => {
    navigate('/auth?role=caregiver');
  };

  const handleWatchDemo = () => {
    // TODO: Implement demo video modal
    console.log('Watch demo clicked');
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm20 0c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          {/* Left Column - Content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                  fontWeight: 'bold',
                  mb: 3,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                Ready to Get Started?
              </Typography>
              
              <Typography
                variant="h6"
                component="p"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  lineHeight: 1.6,
                  mb: 4,
                  opacity: 0.9,
                  maxWidth: { md: '500px' },
                }}
              >
                Join thousands of patients and caregivers who trust Careva for 
                quality healthcare services. Get started in minutes and experience 
                the difference professional care can make.
              </Typography>

              {/* CTA Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  mb: 4,
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: 'white',
                    color: theme.palette.primary.main,
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: theme.palette.grey[100],
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Find Care Now
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleBecomeCaregiver}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontSize: '1.1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Become a Caregiver
                </Button>
              </Box>

              {/* Trust Indicators */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 3,
                  alignItems: 'center',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  opacity: 0.9,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">✓</Typography>
                  <Typography variant="body2">Free to join</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">✓</Typography>
                  <Typography variant="body2">No setup fees</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">✓</Typography>
                  <Typography variant="body2">Cancel anytime</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Column - Visual/Demo */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: { xs: '300px', md: '400px' },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '400px',
                  height: '300px',
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: 4,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                  },
                }}
                onClick={handleWatchDemo}
              >
                <PlayIcon sx={{ fontSize: '4rem', mb: 2, opacity: 0.9 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                  See How It Works
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Watch a quick demo of the Careva platform
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Stats */}
        <Box
          sx={{
            mt: 8,
            pt: 6,
            borderTop: '1px solid rgba(255,255,255,0.2)',
            textAlign: 'center',
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={4}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                10K+
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Happy Users
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                4.9★
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Average Rating
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                24/7
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Support
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};
